import React from 'react';
import { Box, Typography, Paper, Button, Stack } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from '../../utils/dayjs';
import type { Dayjs } from 'dayjs';

interface ReportLayoutProps {
  title: string;
  children: React.ReactNode;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (d: Dayjs | null) => void;
  onEndDateChange: (d: Dayjs | null) => void;
  onExport?: () => void;
  extraFilters?: React.ReactNode;
  loading?: boolean;
}

export const ReportLayout: React.FC<ReportLayoutProps> = ({
  title,
  children,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onExport,
  extraFilters,
  loading,
}) => (
  <Box sx={{ p: { xs: 1, sm: 2 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="h5" sx={{ flexGrow: 1 }}>
        {title}
      </Typography>
      <DatePicker
        label="From"
        value={startDate}
        onChange={onStartDateChange}
        slotProps={{ textField: { size: 'small' } }}
      />
      <DatePicker
        label="To"
        value={endDate}
        onChange={onEndDateChange}
        slotProps={{ textField: { size: 'small' } }}
      />
      {extraFilters}
      {onExport && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={onExport}
          disabled={loading}
        >
          Export CSV
        </Button>
      )}
    </Stack>
    <Paper sx={{ p: 2 }}>{children}</Paper>
  </Box>
);

// CSV export helper
export function exportToCsv(filename: string, rows: Record<string, any>[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const val = row[h];
          if (val === null || val === undefined) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// Hook for common report date state — defaults to last 12 months
export function useReportDates() {
  const [startDate, setStartDate] = React.useState<Dayjs | null>(
    dayjs().subtract(12, 'months').startOf('day')
  );
  const [endDate, setEndDate] = React.useState<Dayjs | null>(
    dayjs().endOf('day')
  );
  return { startDate, endDate, setStartDate, setEndDate };
}

// Hook for reports that default to the current calendar month
export function useCurrentMonthDates() {
  const [startDate, setStartDate] = React.useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = React.useState<Dayjs | null>(
    dayjs().endOf('month')
  );
  return { startDate, endDate, setStartDate, setEndDate };
}
